import React, { useState, useEffect } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Button, TextField,
  Dialog, DialogContent, Grid, IconButton, MenuItem, useTheme, useMediaQuery, Card, CardContent, Divider, Chip
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import API from "../API/API";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

const api = new API();

const formatUserName = (name) => {
  if (!name) return "";
  const lower = name.toLowerCase().trim();
  if (lower === "ramesh admin") {
    return "ramesh Admin";
  }
  return name.replace(/ramesh/gi, "ramesh");
};

export default function DayAmountTransactions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");

  const [activePeriod, setActivePeriod] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Edit Modal State
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [users, setUsers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);

  useEffect(() => {
    fetchTransactions();
    fetchSummary();
  }, [selectedDate]);

  useEffect(() => {
    fetchUsers();
    fetchAdditionalServices();
  }, []);

  const fetchAdditionalServices = async () => {
    try {
      const res = await api.get("api/additional-services");
      const serviceData = res?.data?.data || res?.data || [];
      setAdditionalServices(serviceData);
    } catch (e) {
      console.error("Failed to fetch additional services", e);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get("api/users");
      const userData = res?.data?.data || res?.data || [];
      if (Array.isArray(userData)) {
        setUsers(userData);
      } else if (userData && typeof userData === 'object') {
        const possibleArray = userData.users || userData.list || [];
        setUsers(Array.isArray(possibleArray) ? possibleArray : []);
      }
    } catch (e) {
      console.error("Failed to fetch users", e);
    }
  };

  const fetchPaymentModes = async (companyName) => {
    try {
      const url = companyName ? `api/payment-modes?company_name=${companyName}` : "api/payment-modes";
      const res = await api.get(url);
      setPaymentModes(res?.data?.data || res?.data || []);
    } catch (e) {
      console.error("Failed to fetch payment modes", e);
    }
  };

  const fetchSummary = async () => {
    try {
      const dateStr = dayjs(selectedDate).format("YYYY-MM-DD");
      const res = await api.get(`api/day-amount-transactions/summary?date=${dateStr}`);
      setSummary(res?.data || null);
    } catch (e) {
      console.error("Failed to fetch summary", e);
    }
  };

  const fetchTransactions = async (period = activePeriod) => {
    setLoading(true);
    try {
      let url = "";
      if (period) {
        url = `api/day-amount-transactions?period=${period}`;
      } else {
        const dateStr = dayjs(selectedDate).format("YYYY-MM-DD");
        url = `api/day-amount-transactions?date=${dateStr}`;
      }
      const res = await api.get(url);
      setData(res?.data?.data || res?.data || []);
    } catch (e) {
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setActivePeriod(null);
    if (!searchQuery.trim()) {
      fetchTransactions(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`api/day-amount-transactions/search?guest_name=${searchQuery}`);
      setData(res?.data?.data || res?.data || []);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    if (item.status?.toLowerCase() === "checked-out") {
      toast.info("Checked-Out records cannot be edited");
      return;
    }
    const cash = parseFloat(item.cash) || 0;
    const upi = parseFloat(item.upi) || 0;
    const card = parseFloat(item.card) || 0;
    const credit = parseFloat(item.credit) || 0;
    const bank = parseFloat(item.bank) || 0;
    const cheque = parseFloat(item.cheque) || 0;
    const refund = parseFloat(item.refund) || 0;

    const currentAmount = cash + upi + card + credit + bank + cheque + refund;
    const mode = cash > 0 ? "CASH" : (upi > 0 ? "UPI" : (card > 0 ? "CARD" : (credit > 0 ? "CREDIT" : (bank > 0 ? "BANK" : (cheque > 0 ? "CHEQUE" : "CASH")))));

    let matchingUser = users.find(u => u.name?.toUpperCase() === item.tariff_by?.toUpperCase() || u.name?.toUpperCase() === item.user_name?.toUpperCase())?.name || item.tariff_by || item.user_name || "ADMIN";

    fetchPaymentModes(item.company_name);

    setEditData({
      ...item,
      old_amount: item.old_amount || currentAmount.toFixed(2),
      old_pay_mode: item.old_pay_mode || mode,
      new_receipt_amount: "",
      trans_mode: "",
      adv_type: (item.payment_type || item.adv_type || "").trim().toUpperCase(),
      tariff_by: (matchingUser || "").trim().toUpperCase(),
      room_number: item.room_number || "N/A",
      guest_name: item.check_in_guest_name || item.guest_name || "N/A",
      room_type: item.booking_type || "N/A",
      booking_type: item.room_type || "N/A",
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editData) return;
    try {
      const mode = (editData.trans_mode || "CASH").toUpperCase();
      const amount = parseFloat(editData.new_receipt_amount || 0);

      const payload = {
        ...editData,
        cash: 0, upi: 0, card: 0, credit: 0, bank: 0, cheque: 0,
        user_name: formatUserName(editData.tariff_by || editData.user_name),
        tariff_by: formatUserName(editData.tariff_by),
        adv_type: editData.adv_type,
        old_amount: amount.toFixed(2),
        old_pay_mode: mode,
        receipt_no: `RCP-${editData.id?.toString().padStart(4, '0')}-${mode} ${amount}/-`,
        payment_type: editData.adv_type
      };

      payload[mode.toLowerCase()] = amount;

      await api.put(`api/day-amount-transactions/${editData.id}`, payload);
      toast.success("Transaction updated successfully");
      setEditOpen(false);
      fetchTransactions();
      fetchSummary();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const handleDelete = async () => {
    if (!editData || !window.confirm("Are you sure you want to delete this transaction?")) return;
    try {
      await api.delete(`api/day-amount-transactions/${editData.id}`);
      toast.success("Transaction deleted successfully");
      setEditOpen(false);
      fetchTransactions();
      fetchSummary();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const tableHeaderStyle = {
    bgcolor: "#0b1120",
    color: "#00d7d7",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "bold",
    border: "1px solid #1c2b4d",
    textAlign: "center",
    whiteSpace: "nowrap",
    py: 1,
    px: isMobile ? 1 : 2
  };

  const cellStyle = {
    color: "#fff",
    fontSize: isMobile ? "10px" : "11px",
    fontWeight: "500",
    border: "1px solid #1c2b4d",
    textAlign: "center",
    whiteSpace: "nowrap",
    py: 0.5,
  };

  const cleanPaymentType = (type) => {
    if (!type) return "";
    return type.replace(/^ADVANCE:\s*/i, "").trim();
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2, bgcolor: "#111C31", minHeight: "100vh" }}>
      {/* Search & Filter Header */}
      <Box sx={{ mb: 2, bgcolor: '#0e1729', p: isMobile ? 1.5 : 2, borderRadius: '8px', border: '1px solid #253050' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={{ color: '#4B90FC', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', textAlign: isMobile ? 'center' : 'left' }}>
              Day Amount Transactions
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={9}>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5, alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-end' }}>
              <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                <TextField
                  fullWidth={isMobile}
                  size="small"
                  placeholder="Search Guest..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{
                    bgcolor: '#1a2035',
                    '& .MuiOutlinedInput-root': {
                      color: '#e0e6f0',
                      fontSize: '12px',
                      height: '36px',
                      '& fieldset': { borderColor: '#3a4a6b' },
                    }
                  }}
                />
                <Button variant="contained" onClick={handleSearch} sx={{ bgcolor: '#4B90FC', fontSize: '11px', minWidth: '80px' }}>
                  SEARCH
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
                <Typography sx={{ color: '#8faac8', fontSize: '11px', fontWeight: 'bold' }}>Date:</Typography>
                <Box className="custom-datepicker" sx={{ flexGrow: isMobile ? 1 : 0 }}>
                    <DatePicker
                    selected={selectedDate}
                    onChange={(date) => {
                        setActivePeriod(null);
                        setSelectedDate(date);
                    }}
                    dateFormat="dd-MM-yyyy"
                    customInput={
                        <TextField
                        size="small"
                        sx={{
                            bgcolor: '#1a2035',
                            '& .MuiOutlinedInput-root': {
                            color: '#e0e6f0',
                            fontSize: '12px',
                            height: '36px',
                            width: isMobile ? '100%' : '110px',
                            '& fieldset': { borderColor: '#3a4a6b' },
                            }
                        }}
                        />
                    }
                    />
                </Box>
                <IconButton onClick={() => { fetchTransactions(); fetchSummary(); }} sx={{ color: '#4B90FC', bgcolor: '#1a2035', borderRadius: 1 }}>
                    <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Period Tags */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, justifyContent: isMobile ? 'center' : 'flex-end' }}>
          {[
            { label: "Month", value: "this_month" },
            { label: "Yesterday", value: "yesterday" },
            { label: "Today", value: "today" }
          ].map(p => (
            <Button
              key={p.value}
              variant={activePeriod === p.value ? "contained" : "outlined"}
              onClick={() => {
                setActivePeriod(p.value);
                fetchTransactions(p.value);
              }}
              sx={{
                fontSize: '10px',
                fontWeight: 'bold',
                height: '28px',
                px: 2,
                color: activePeriod === p.value ? '#fff' : '#ff3333',
                borderColor: '#ff3333',
                bgcolor: activePeriod === p.value ? '#ff3333' : 'transparent',
                '&:hover': { bgcolor: activePeriod === p.value ? '#cc2929' : 'rgba(255, 51, 51, 0.1)' }
              }}
            >
              {p.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Summary Section (Horizontal Scrollable on Mobile) */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
        {summary && ['today', 'this_month'].map((key) => (
          <Paper key={key} elevation={3} sx={{ p: 1.5, minWidth: isMobile ? '280px' : '350px', bgcolor: '#0e1729', border: '1px solid #253050' }}>
            <Typography variant="caption" sx={{ color: '#4B90FC', fontWeight: 'bold', textTransform: 'uppercase', mb: 1, display: 'block' }}>
              {key === 'today' ? "Today's Summary" : "This Month's Summary"}
            </Typography>
            <Grid container spacing={1}>
              {['Cash', 'UPI', 'Card', 'Credit', 'Bank', 'Cheque'].map(mode => (
                <Grid item xs={4} key={mode}>
                  <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: '#1a2035', borderRadius: 1 }}>
                    <Typography sx={{ color: '#8faac8', fontSize: '9px' }}>{mode}</Typography>
                    <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>{summary[key]?.[mode.toLowerCase()] || "0.00"}</Typography>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5, bgcolor: '#253050' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                   <Box>
                    <Typography sx={{ color: '#8faac8', fontSize: '10px' }}>Ref</Typography>
                    <Typography sx={{ color: '#ff3333', fontSize: '12px', fontWeight: 'bold' }}>{summary[key]?.refund || "0.00"}</Typography>
                   </Box>
                   <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ color: '#4B90FC', fontSize: '11px', fontWeight: 'bold' }}>TOTAL</Typography>
                    <Typography sx={{ color: '#ffd700', fontSize: '16px', fontWeight: 'bold' }}>{summary[key]?.total || "0.00"}</Typography>
                   </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Box>

      {/* Main Table / Mobile List */}
      <Box sx={{ border: "1px solid #1c2b4d", borderRadius: "8px", overflow: "hidden", bgcolor: "#111c31" }}>
        <Box sx={{ py: 1.5, borderBottom: "1px solid #1c2b4d", textAlign: "center", bgcolor: "#000" }}>
          <Typography sx={{ color: "#ffd700", fontWeight: "bold", fontSize: isMobile ? "13px" : "15px", letterSpacing: "1px", textTransform: "uppercase" }}>
            Transactions for {dayjs(selectedDate).format("DD-MM-YYYY")}
          </Typography>
        </Box>

        {isMobile ? (
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 'calc(100vh - 430px)', overflowY: 'auto' }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
            ) : data.length === 0 ? (
                <Typography align="center" sx={{ color: '#8899bb', py: 5 }}>No records found</Typography>
            ) : (
                data.map((item) => (
                    <Card key={item.id} variant="outlined" sx={{ bgcolor: '#0e1729', borderColor: '#253050', color: '#fff' }} onClick={() => handleEditClick(item)}>
                        <CardContent sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ color: '#4B90FC', fontWeight: 'bold', fontSize: '13px' }}>ID: {item.booking_id || item.id}</Typography>
                                <Typography sx={{ color: '#ffd700', fontWeight: 'bold', fontSize: '13px' }}>Room: {item.room_number || "-"}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '15px', fontWeight: 'bold', mb: 0.5 }}>{item.guest_name}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ color: '#8faac8', fontSize: '11px' }}>{cleanPaymentType(item.payment_type)}</Typography>
                                <Chip label={item.receipt_no?.split('-').pop() || "CASH"} size="small" sx={{ height: 18, fontSize: '9px', bgcolor: '#1a2035', color: '#00d7d7' }} />
                            </Box>
                            <Divider sx={{ my: 1, bgcolor: '#253050' }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                <Box>
                                    <Typography sx={{ color: '#8faac8', fontSize: '10px' }}>User: {formatUserName(item.user_name)}</Typography>
                                    <Typography sx={{ color: '#8faac8', fontSize: '10px' }}>Time: {dayjs(`2000-01-01 ${item.time}`).format("hh:mm A")}</Typography>
                                </Box>
                                <Typography sx={{ color: '#00ff00', fontSize: '18px', fontWeight: 'bold' }}>
                                    ₹{parseFloat(item.total || item.cash || item.upi || item.card || item.credit || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
          </Box>
        ) : (
          <TableContainer sx={{
            maxHeight: "calc(100vh - 450px)",
            '&::-webkit-scrollbar': { height: '8px', width: '8px' },
            '&::-webkit-scrollbar-track': { bgcolor: '#0e1729' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#3a4a6b', borderRadius: '4px' },
            overflowX: 'auto'
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderStyle}>Booking ID</TableCell>
                  <TableCell sx={tableHeaderStyle}>R-SHIFT</TableCell>
                  <TableCell sx={tableHeaderStyle}>Room No</TableCell>
                  <TableCell sx={tableHeaderStyle}>Guest Name</TableCell>
                  <TableCell sx={tableHeaderStyle}>Payment Type</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, bgcolor: '#1a2035' }}>Cash</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, bgcolor: '#1a2035' }}>UPI</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, bgcolor: '#1a2035' }}>Card</TableCell>
                  <TableCell sx={{ ...tableHeaderStyle, bgcolor: '#1a2035' }}>TOTAL</TableCell>
                  <TableCell sx={tableHeaderStyle}>User Name</TableCell>
                  <TableCell sx={tableHeaderStyle}>Time</TableCell>
                  <TableCell sx={tableHeaderStyle}>Receipt No</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={12} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                ) : data.length === 0 ? (
                  <TableRow><TableCell colSpan={12} align="center" sx={{ py: 5, color: '#8899bb' }}>No transactions found</TableCell></TableRow>
                ) : (
                  data.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      onDoubleClick={() => handleEditClick(item)}
                      onClick={() => setSelectedRow(item.id)}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(75, 144, 252, 0.1)' },
                        cursor: 'pointer',
                        bgcolor: selectedRow === item.id ? 'rgba(75, 144, 252, 0.2) !important' : 'transparent'
                      }}
                    >
                      <TableCell sx={cellStyle}>{item.booking_id || "-"}</TableCell>
                      <TableCell sx={cellStyle}>{item.shifted_from ? `${item.shifted_from} >>` : ""}</TableCell>
                      <TableCell sx={cellStyle}>{item.room_number}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>{item.guest_name}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'left' }}>{cleanPaymentType(item.payment_type)}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(item.cash || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(item.upi || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(item.card || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', color: '#00ff00' }}>
                        {parseFloat(item.total || item.cash || item.upi || item.card || 0).toFixed(2)}
                      </TableCell>
                      <TableCell sx={cellStyle}>{formatUserName(item.user_name)}</TableCell>
                      <TableCell sx={cellStyle}>{dayjs(`2000-01-01 ${item.time}`).format("hh:mm A")}</TableCell>
                      <TableCell sx={cellStyle}>{item.receipt_no}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth={isMobile}
        PaperProps={{
          sx: {
            bgcolor: '#0e1729',
            color: '#e0e6f0',
            border: '1px solid #253050',
            borderRadius: isMobile ? '8px' : '0px'
          }
        }}
      >
        <Box sx={{ bgcolor: '#008080', py: 1.5, px: 2 }}>
          <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '16px' }}>
            Customer Advances Details
          </Typography>
        </Box>

        <DialogContent sx={{ mt: 0, bgcolor: '#0e1729', p: isMobile ? 2 : 4 }}>
          <Grid container spacing={isMobile ? 2 : 4}>
            {/* Form Fields */}
            {[
              { label: 'Room No:', field: 'room_number', disabled: true },
              { label: 'Guest Name:', field: 'guest_name', disabled: true },
              { label: 'Receipt Date:', field: 'date', type: 'date' },
              { label: 'Time:', field: 'time', type: 'time' },
              { label: 'New Amount:', field: 'new_receipt_amount', color: '#4B90FC' },
              { label: 'Mode:', field: 'trans_mode', type: 'select', options: paymentModes.map(m => (m.mode_name || m.name || m.option_name || (typeof m === 'string' ? m : "")).trim().toUpperCase()).filter(Boolean) },
              { label: 'Type:', field: 'adv_type', type: 'select', options: ["ROOM ADVANCE", "RESERVATION ADV", "CHECK PAY", "EXTRA CHARGES"] },
              { label: 'Tariff By:', field: 'tariff_by', type: 'select', options: users.map(u => (u.name || "").trim().toUpperCase()) },
            ].map(f => (
              <Grid item xs={12} sm={6} key={f.field}>
                <Typography sx={{ color: '#8faac8', fontSize: '12px', mb: 0.5 }}>{f.label}</Typography>
                <TextField
                  fullWidth
                  select={f.type === 'select'}
                  size="small"
                  disabled={f.disabled}
                  type={f.type === 'date' || f.type === 'time' ? f.type : 'text'}
                  value={editData?.[f.field] || ""}
                  onChange={(e) => handleChange(f.field, e.target.value)}
                  sx={{
                    bgcolor: '#1a2035',
                    '& .MuiOutlinedInput-root': {
                      color: f.color || '#e0e6f0',
                      fontSize: '13px',
                      height: '36px',
                      '& fieldset': { borderColor: '#3a4a6b' },
                    }
                  }}
                >
                  {f.type === 'select' && f.options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </TextField>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, p: 3, bgcolor: '#0e1729' }}>
          <Button onClick={handleDelete} variant="contained" color="error" size={isMobile ? "small" : "medium"}>DELETE</Button>
          {!isMobile && <Button onClick={() => window.print()} variant="contained" sx={{ bgcolor: '#4B0082' }}>PRINT</Button>}
          <Button onClick={() => setEditOpen(false)} variant="contained" sx={{ bgcolor: '#008080' }} size={isMobile ? "small" : "medium"}>CLOSE</Button>
          <Button onClick={handleUpdate} variant="contained" color="success" size={isMobile ? "small" : "medium"}>UPDATE</Button>
        </Box>
      </Dialog>
    </Box>
  );
}
